/**
 * RoutePin Component
 *
 * Renders a single route marker on the gym floor plan map as a filled circle.
 *
 * Features:
 * - Inner fill from route's hold color
 * - Optional outer ring from tag color
 * - Auto-contrasting text color (white or dark)
 * - Completion indicator (faded + checkmark for sent routes)
 * - Click/tap for route selection
 *
 * @example
 * ```tsx
 * <RoutePin
 *   x={200}
 *   y={150}
 *   grade="V3"
 *   holdColor="#FF0000"
 *   tagColor="#0000FF"
 *   isSent={false}
 *   onClick={() => handleRouteClick(route)}
 * />
 * ```
 */

import { useMemo, useCallback, useState, memo } from 'react';
import { Circle, Text, Group, Path } from 'react-konva';
import type Konva from 'konva';

/**
 * Props for RoutePin component
 */
export interface RoutePinProps {
  /** X position in pixel coordinates */
  x: number;
  /** Y position in pixel coordinates */
  y: number;
  /** Grade label to display (e.g., "V3") */
  grade: string;
  /** Inner fill color (hold color, or fallback) */
  holdColor: string;
  /** Outer ring color (tag color). If omitted, no ring. */
  tagColor?: string;
  /** Whether this route has been sent by the user */
  isSent?: boolean;
  /** Whether this pin is currently selected */
  isSelected?: boolean;
  /** Diameter of the pin in pixels (default: 28) */
  size?: number;
  /** Called when pin is clicked */
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  /** Called when pin is tapped (touch) */
  onTap?: (event: Konva.KonvaEventObject<TouchEvent>) => void;
  /** Unique identifier */
  id?: string;
}

/**
 * Calculate whether text should be light or dark based on background color.
 * Uses relative luminance formula.
 */
const getContrastingTextColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // If parsing failed, default to white
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return '#FFFFFF';
  }

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return dark text for light backgrounds, white text for dark backgrounds
  return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
};

/**
 * Check if a hex color is very light (luminance > 0.85).
 * Used to add a subtle border so light pins remain visible on white backgrounds.
 */
const isLightColor = (hexColor: string): boolean => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return false;
  }

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.85;
};

/**
 * Checkmark SVG path for sent indicator.
 * Small checkmark positioned in bottom-right of the pin.
 */
const CHECKMARK_PATH = 'M2,6 L5,9 L10,3';

/**
 * Default pin size in pixels
 */
const DEFAULT_SIZE = 28;

/**
 * Opacity values for pin states
 */
const OPACITY_NORMAL = 1;
const OPACITY_SENT = 0.5;

/**
 * RoutePin Component
 *
 * Renders a route marker as a colored circle with grade label.
 * Shows completion status with faded appearance and checkmark badge.
 */
const RoutePinBase = ({
  x,
  y,
  grade,
  holdColor,
  tagColor,
  isSent = false,
  isSelected: _isSelected = false,
  size = DEFAULT_SIZE,
  onClick,
  onTap,
  id,
}: RoutePinProps) => {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate text color based on background
  const textColor = useMemo(() => getContrastingTextColor(holdColor), [holdColor]);

  // Calculate font size based on pin size and grade length
  const fontSize = useMemo(() => {
    const baseSize = size * 0.4;
    // Reduce font size for longer grades
    if (grade.length > 3) {
      return baseSize * 0.8;
    }
    return baseSize;
  }, [size, grade]);

  // Calculate opacity based on sent status
  const opacity = isSent ? OPACITY_SENT : OPACITY_NORMAL;

  // Checkmark badge position and size
  const checkmarkSize = size * 0.35;
  const checkmarkOffset = size * 0.35;

  // Handle mouse events
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Handle click with callback
  const handleClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      onClick?.(e);
    },
    [onClick]
  );

  // Handle tap with callback
  const handleTap = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      onTap?.(e);
    },
    [onTap]
  );

  // Scale factor for proportional stroke widths
  // All stroke widths are relative to the DEFAULT_SIZE (28px)
  const scaleFactor = size / DEFAULT_SIZE;

  // Determine stroke based on state (hover > tagColor > light border > default)
  // Selected pins are made larger instead of changing stroke
  // All stroke widths scale proportionally with pin size
  const { strokeColor, strokeWidth } = useMemo(() => {
    if (isHovered) {
      // On hover, use tag color ring if present, otherwise white
      return { strokeColor: tagColor ?? '#FFFFFF', strokeWidth: 2 * scaleFactor };
    }
    if (tagColor) {
      return { strokeColor: tagColor, strokeWidth: 4 * scaleFactor };
    }
    if (isLightColor(holdColor)) {
      return { strokeColor: 'rgba(0,0,0,0.15)', strokeWidth: 1 * scaleFactor };
    }
    return { strokeColor: 'rgba(0,0,0,0.2)', strokeWidth: 1 * scaleFactor };
  }, [isHovered, tagColor, holdColor, scaleFactor]);

  // Build group props conditionally
  const groupProps: Konva.GroupConfig = {
    x,
    y,
    opacity,
  };

  if (id) {
    groupProps.id = id;
  }

  const radius = size / 2;
  // Outer outline stroke width also scales proportionally
  const outlineStrokeWidth = 1.5 * scaleFactor;

  return (
    <Group {...groupProps}>
      {/* Outer outline for all pins to help them stand out from background */}
      <Circle
        radius={radius + strokeWidth / 2 + outlineStrokeWidth}
        fill="transparent"
        stroke="rgba(100,100,100,0.35)"
        strokeWidth={outlineStrokeWidth}
        listening={false}
        perfectDrawEnabled={false}
      />
      {/* Main circle shape - shadows removed for performance */}
      <Circle
        radius={radius}
        fill={holdColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        perfectDrawEnabled={false}
        shadowForStrokeEnabled={false}
        hitStrokeWidth={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTap={handleTap}
      />

      {/* Grade label - centered inside circle */}
      <Text
        x={-size / 2}
        y={-fontSize / 2}
        text={grade}
        fontSize={fontSize}
        fontStyle="bold"
        fill={textColor}
        align="center"
        width={size}
        height={fontSize}
        listening={false}
      />

      {/* Checkmark badge for sent routes */}
      {isSent && (
        <Group x={checkmarkOffset} y={checkmarkOffset}>
          {/* Badge background circle */}
          <Circle
            x={0}
            y={0}
            radius={checkmarkSize / 2}
            fill="#10B981"
            listening={false}
            perfectDrawEnabled={false}
          />
          {/* Checkmark path - centered within badge circle */}
          {/* Path coords are in 12x12 space with center at (6,6), scaled to checkmarkSize */}
          <Path
            x={-checkmarkSize / 2}
            y={-checkmarkSize / 2}
            data={CHECKMARK_PATH}
            stroke="#FFFFFF"
            strokeWidth={1.5 * scaleFactor}
            lineCap="round"
            lineJoin="round"
            scaleX={checkmarkSize / 12}
            scaleY={checkmarkSize / 12}
            listening={false}
          />
        </Group>
      )}
    </Group>
  );
};

// Memoize to prevent unnecessary re-renders during map pan/zoom
export const RoutePin = memo(RoutePinBase);

export default RoutePin;
